import React, { Fragment, useState } from 'react';
import { useRouteMatch, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { useApolloClient, useLazyQuery, gql } from '@apollo/client';
import GlobalStyles from '@minecraft.global-styles';
import LoginPageRedirection from '@minecraft.login-page-redirection';
import { CoreTheme } from '@minecraft.themes';
import {
  gqlSystemRoleCode as SYSTEM_ROLE,
  gqlAccountOnboardingStatusEnum as ACCOUNT_ONBOARDING_STATUS,
} from '@minecraft.graphql-types';
import { parseJwt, getOnboardingToken } from '@blocs.auth';
import { useRouter } from '@blocs.router';
import { GET_PRIMARY_ACCOUNT_ID, GET_USER_STATUS, GET_USER_STEP } from 'graphqls/profile';
import { AppGlobalStyles, StyledPage, Inner } from 'styles/Page';
import LoaderContext from 'lib/loaderContext';
import FullLoader from 'common/FullLoader';
import { loggedOutRoutes } from 'constants/logoutRoutes.constants';
import StepsProgressBar from 'common/StepsProgressBar';
import { getTalentFlowStepsData } from 'utils/stepper';
import routeHelper from 'src/routes/helper';
import { ulaUrl } from 'constants/env.constants';
import { VerifiedAccountRedirect } from 'components/VerifiedAccount/VerifiedAccount';
import Meta from './Meta';
import Header from './Header';

type WrapperProps = {
  children: JSX.Element;
};

export const GET_ONBOARDING_STATUS = gql`
  query currentUser {
    currentUser {
      onboardingStatus
      email
      primaryAccountId
    }
  }
`;

const LoginRedirectionWrapper = ({ children }: WrapperProps): JSX.Element => {
  const client = useApolloClient();
  const { navigate } = useRouter();
  const token = getOnboardingToken();
  const parsedToken = parseJwt(token);

  const isTalent = parsedToken ? parsedToken.roles.includes(SYSTEM_ROLE.TALENT) : false;
  const location = useLocation();
  const { pathname } = location;
  const pathToAvoidLogin = ['/max-claims-reached'];
  const hideMainLayout = pathname === routeHelper.root.href();

  const isResetEmail = pathname === routeHelper.resetEmail.href();
  // we need to fetch user data only on some of the pages and not all
  const refetchCurrentUser = useRouteMatch({
    path: [
      routeHelper.additionalClaimWelcome.href(),
      routeHelper.createAccount.href(),
      routeHelper.verifiedEmail.href(),
    ],
    strict: true,
    sensitive: false,
  });

  const [getPrimaryAccountId, { data: primaryAccountId }] = useLazyQuery<{ primaryAccountId: number }>(
    GET_PRIMARY_ACCOUNT_ID
  );

  React.useEffect(() => {
    const getCurrentUser = async (): Promise<void> => {
      try {
        const { data } = await client.query({
          query: GET_ONBOARDING_STATUS,
          fetchPolicy: 'no-cache',
        });

        // do not redirect to verify email if user just set status to verified

        if (
          (!data.currentUser.primaryAccountId &&
            data.currentUser.onboardingStatus === ACCOUNT_ONBOARDING_STATUS.BAU_UNVERIFIED &&
            pathname !== routeHelper.verifiedAccount.href()) ||
          (!data.currentUser.primaryAccountId &&
            data.currentUser.onboardingStatus === ACCOUNT_ONBOARDING_STATUS.BAU_VERIFIED)
        ) {
          const { data: userStatus } = await client.query({
            query: GET_USER_STATUS,
          });

          if (!userStatus?.isVerified) {
            client.cache.writeQuery({
              query: GET_USER_STATUS,
              data: { isVerified: true },
            });

            client.cache.writeQuery({
              query: GET_USER_STEP,
              data: { onboardingStep: '' },
            });
          }

          VerifiedAccountRedirect(token, client, true, navigate);
        }
      } catch (error) {
        localStorage.clear();
        window.location.replace(`${ulaUrl}?${new URLSearchParams({ error: 'Error in fetching current user data' })}`);
      }
    };

    if (token) {
      if (refetchCurrentUser) {
        getCurrentUser().catch((e) => console.error('Error getting current user', { error: e }));
      }

      if (!isResetEmail) {
        getPrimaryAccountId();
      }
    }
  }, [client, pathname, token, isResetEmail, getPrimaryAccountId]);

  if (pathname && pathToAvoidLogin.includes(pathname)) {
    return <Inner>{children}</Inner>;
  }

  return (
    <LoginPageRedirection
      loggedOutRoutes={loggedOutRoutes}
      token={token}
      actualRouterPathName={pathname}
      redirectToLoginPage={(): void => {
        localStorage.clear();
        window.location.replace(ulaUrl);
      }}
    >
      <>
        {!hideMainLayout && token && <Header />}
        {isTalent && <StepsProgressBar data={getTalentFlowStepsData(pathname, primaryAccountId?.primaryAccountId)} />}
        <Inner>{children}</Inner>
      </>
    </LoginPageRedirection>
  );
};

const Page = ({ children }: { children: JSX.Element }): JSX.Element => {
  const [showLoader, setShowLoader] = useState(false);

  const loaderVisibility = (visibility: boolean): void => {
    if (showLoader !== visibility || !visibility) {
      setShowLoader(visibility);
    }
  };

  return (
    <ThemeProvider theme={CoreTheme}>
      <>
        <GlobalStyles />
        <AppGlobalStyles />
        <LoaderContext.Provider value={{ showLoader, loaderVisibility }}>
          {showLoader && <FullLoader />}
          <StyledPage>
            <Meta />
            <LoginRedirectionWrapper>{children}</LoginRedirectionWrapper>
          </StyledPage>
        </LoaderContext.Provider>
      </>
    </ThemeProvider>
  );
};

export default Page;
